import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import EnrollmentForm from "./components/EnrollmentForm";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";

const courses = [
  {
    id: "ion-anti-aging",
    tone: "bg-pink-100",
    ring: "ring-pink-200",
    title: "離子紅光逆齡抗衰老 課程",
    meta: [
      { label: "堂數", value: "4 天實操 + 線上理論" },
      { label: "時長", value: "約 24 小時" },
      { label: "適合對象", value: "美容師、醫美從業員、轉行人士" },
    ],
    content: [
      "眉稜提升",
      "去除川字紋、法令紋、木偶紋上下、鼻紋、鼻背紋、抬頭紋、夫妻宮、下顎線、側面輪廓線、雙下巴、老人凹、頭紋、耳紋",
    ],
    include: "線上理論教學影片 / 4 天實操教授和考核 / 超離子儀器 / ISHB 基礎證書",
    img: "https://storage.helloreaddy.io/project_files/5083fe3c-0446-4f05-ab30-834aed56ded7/cc3e850b-1137-4fb4-8895-72deb694f468_compressed_-62.webp",
  },
  {
    id: "ion-stretch-mark",
    tone: "bg-purple-100",
    ring: "ring-purple-200",
    title: "離子紅光去妊娠紋課程",
    meta: [
      { label: "堂數", value: "3 天實操 + 線上理論" },
      { label: "時長", value: "約 18 小時" },
      { label: "適合對象", value: "產後護理師、美容師、孕產媽媽" },
    ],
    content: ["妊娠紋分類：輕度、中度、重度", "去除肥胖紋姙娠紋", "去脫孕婦妊娠紋"],
    include: "線上理論教學影片 / 3 天實操教授和考核 / 超離子儀器 / ISHB 基礎證書",
    img: "https://storage.helloreaddy.io/project_files/5083fe3c-0446-4f05-ab30-834aed56ded7/6b8d5618-bbed-4e0b-af86-d3a7c6b56a8d_compressed_-63.webp",
  },
  {
    id: "ai-postpartum",
    tone: "bg-indigo-100",
    ring: "ring-indigo-200",
    title: "高階全科產後課程",
    meta: [
      { label: "堂數", value: "4 天實操 + 線上理論" },
      { label: "時長", value: "約 30 小時" },
      { label: "適合對象", value: "產後修復師、中醫美容從業員" },
    ],
    content: [
      "全港唯一高階產後課程",
      "從影像評估到為客人度身訂造專屬紮肚方程式 7 大絕招，讓療效為產後修復界的獨尖專家",
      "紮肚技術實堂 · 盆骨檢閉評估 + 儀器教學 · 靈宮穴位評估 + 儀器教學",
      "中醫面診見茶托腹技術 · 中醫高階收盆技術",
    ],
    include: "線上理論 / 4 天實操教授和考核 / ISHB 基礎證書",
    img: "https://storage.helloreaddy.io/project_files/5083fe3c-0446-4f05-ab30-834aed56ded7/72355ee3-cc98-4830-a4ff-09029cbe3832_compressed_-61.webp",
  },
];

export default function Courses() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [favLoadingId, setFavLoadingId] = useState<string | null>(null);
  const [favFeedback, setFavFeedback] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setFavoriteIds(new Set());
      return;
    }
    let mounted = true;
    supabase
      .from("favorites")
      .select("course_id")
      .eq("user_id", user.id)
      .then(({ data, error }) => {
        if (!error && data && mounted) {
          setFavoriteIds(new Set((data as { course_id: string }[]).map((f) => f.course_id)));
        }
      });
    return () => {
      mounted = false;
    };
  }, [user]);

  const toggleFavorite = async (courseId: string, title: string) => {
    if (!user) {
      navigate("/auth?redirect=/courses");
      return;
    }
    setFavLoadingId(courseId);
    setFavFeedback(null);
    try {
      if (favoriteIds.has(courseId)) {
        const { error } = await supabase.from("favorites").delete().eq("user_id", user.id).eq("course_id", courseId);
        if (error) throw error;
        setFavoriteIds((prev) => {
          const next = new Set(prev);
          next.delete(courseId);
          return next;
        });
        setFavFeedback(`已取消收藏「${title}」`);
      } else {
        const { error } = await supabase.from("favorites").insert({ user_id: user.id, course_id: courseId });
        if (error) throw error;
        setFavoriteIds((prev) => new Set(prev).add(courseId));
        setFavFeedback(`已收藏「${title}」`);
      }
    } catch {
      setFavFeedback("操作失敗，請稍後再試");
    } finally {
      setFavLoadingId(null);
      setTimeout(() => setFavFeedback(null), 2500);
    }
  };

  return (
    <div className="min-h-screen bg-brand-50/60 text-brand-900">
      <Navbar />

      <section className="pt-32 md:pt-40 pb-16 md:pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="inline-block px-6 py-2 rounded-md bg-brand-600 text-white text-lg tracking-wider">最新課程</div>
          <h1 className="mt-6 font-serif text-3xl md:text-5xl text-brand-800">離子紅光專業課程</h1>
          <p className="mt-4 text-brand-700/70 max-w-2xl text-sm md:text-base">
            由國際專家團隊親自教授，結合理論與實操，完成考核即獲頒 ISHB 國際認證，開啟專業美業之路。
          </p>
          {user && (
            <div className="mt-4 flex items-center gap-2">
              <Link to="/member" className="inline-flex items-center gap-1.5 text-sm text-brand-600 hover:text-brand-700 cursor-pointer">
                <i className="ri-user-star-line"></i> 查看我的收藏與報名紀錄
              </Link>
            </div>
          )}
        </div>
      </section>

      {favFeedback && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-full bg-brand-900 text-white text-sm shadow-lg whitespace-nowrap">
          {favFeedback}
        </div>
      )}

      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 items-start">
            <div className="lg:col-span-2">
              <div className="grid grid-cols-1 gap-5">
                {courses.map((c) => (
                  <article
                    key={c.id}
                    className={`${c.tone} ring-1 ${c.ring} rounded-lg overflow-hidden flex flex-col sm:flex-row sm:items-start`}
                  >
                    <img
                      src={c.img}
                      alt={c.title}
                      className="w-full sm:w-40 md:w-52 h-auto self-start shrink-0"
                    />
                    <div className="p-5 md:p-6 flex-1 text-[13px] leading-relaxed text-brand-900/85">
                      <div className="flex items-start justify-between gap-3">
                        <h4 className="text-base font-semibold text-brand-900">
                          <a href="#enroll" className="hover:text-brand-700 cursor-pointer">{c.title}</a>
                        </h4>
                        <button
                          onClick={() => toggleFavorite(c.id, c.title)}
                          disabled={favLoadingId === c.id}
                          className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full cursor-pointer transition hover:bg-white/60"
                          aria-label={favoriteIds.has(c.id) ? "取消收藏" : "收藏課程"}
                        >
                          <i className={`${favoriteIds.has(c.id) ? "ri-heart-fill text-brand-600" : "ri-heart-line text-brand-900/50"}`}></i>
                        </button>
                      </div>

                      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                        {c.meta.map((m) => (
                          <div key={m.label} className="rounded-md bg-white/60 px-3 py-2">
                            <div className="text-xs font-medium text-brand-600">{m.label}</div>
                            <div className="mt-0.5 text-sm text-brand-900/85">{m.value}</div>
                          </div>
                        ))}
                      </div>

                      <div className="mt-4">
                        <span className="font-medium">教學內容：</span>
                        <ul className="mt-1 space-y-0.5">
                          {c.content.map((line, i) => (
                            <li key={i} className="flex gap-1">
                              <i className="ri-checkbox-circle-line text-brand-600 mt-0.5"></i>
                              <span>{line}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <p className="mt-3">
                        <span className="font-medium">學費包括：</span>
                        {c.include}
                      </p>
                    </div>
                  </article>
                ))}
              </div>
            </div>

            <div className="lg:sticky lg:top-24">
              <div className="inline-block px-5 py-2 rounded-md bg-brand-900 text-white text-lg tracking-wider mb-6">離子紅光介紹</div>
              <div className="rounded-lg overflow-hidden bg-brand-950/5">
                <video
                  src="https://storage.helloreaddy.io/project_files/5083fe3c-0446-4f05-ab30-834aed56ded7/a482baf5-14c4-446a-8a66-5fbc81cd38ca_YTDown.com_YouTube_Media_OaW9kKRttpc__001_1080p_compressed-1.mp4"
                  controls
                  playsInline
                  preload="metadata"
                  className="w-full h-auto block"
                ></video>
              </div>

              <a
                href="#enroll"
                className="group mt-6 flex items-center justify-center gap-2 w-full px-8 py-4 rounded-md bg-brand-600 hover:bg-brand-700 text-white text-base font-bold whitespace-nowrap cursor-pointer transition-all duration-200 hover:scale-[1.03] active:scale-[0.98] ring-2 ring-brand-500/40"
              >
                立即報名 <i className="ri-arrow-right-line transition-transform duration-200 group-hover:translate-x-1"></i>
              </a>

              {/* 會員優惠提示 */}
              <div className="mt-6 bg-white border border-brand-100 rounded-lg p-5">
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-vip-crown-line text-brand-600 text-xl"></i>
                  <h4 className="font-semibold text-brand-900">會員專屬優惠</h4>
                </div>
                <p className="text-sm text-brand-700/70 leading-relaxed">
                  成為 ISHB 會員以獲取最新課程優惠資訊、累積點數升級會員等級，並解鎖更多專屬福利。
                </p>
                {!user && (
                  <Link
                    to="/auth?redirect=/courses"
                    className="mt-4 inline-flex items-center gap-2 px-5 py-2 rounded-md bg-brand-600 hover:bg-brand-700 text-white text-sm whitespace-nowrap cursor-pointer transition"
                  >
                    立即註冊會員
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <EnrollmentForm />

      <Footer />
    </div>
  );
}